import { useMemo, useState } from 'react';
import { useCrm } from '../store';
import { Card, PageTitle, StatutBadge, Tag } from '../ui';
import { formatDateFR, formatEUR } from '../utils';
import { Mic2, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmDialog';

const formatDateTimeFR = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

type Mode = 'actif' | 'passif';

export default function SuiviArtistes({ embedded = false }: { embedded?: boolean } = {}) {
  const data = useCrm();
  const confirm = useConfirm();
  const [mode, setMode] = useState<Mode>('actif');

  const grouped = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todayTs = startOfToday.getTime();

    const map = new Map<string, typeof data.prest>();
    for (const p of data.prest) {
      if (p.archivedAt) continue;
      const ts = p.dateEvent ? new Date(p.dateEvent).getTime() : null;
      const isPast = ts != null && !isNaN(ts) && ts < todayTs;
      // Actif : événement futur ou sans date. Passif : événement passé.
      if (mode === 'actif' && isPast) continue;
      if (mode === 'passif' && !isPast) continue;

      for (const a of p.artistes) {
        const arr = map.get(a) ?? [];
        arr.push(p);
        map.set(a, arr);
      }
    }
    return Array.from(map.entries())
      .map(([name, prests]) => {
        const sorted = [...prests].sort((a, b) => {
          const ta = a.dateEvent ? new Date(a.dateEvent).getTime() : null;
          const tb = b.dateEvent ? new Date(b.dateEvent).getTime() : null;
          if (mode === 'actif') {
            // Futur le plus proche d'abord ; sans date à la fin
            if (ta == null) return 1;
            if (tb == null) return -1;
            return ta - tb;
          }
          // Passif : passé le plus récent d'abord
          if (ta == null) return 1;
          if (tb == null) return -1;
          return tb - ta;
        });
        return [name, sorted] as const;
      })
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [data.prest, mode]);

  const totalCA = grouped.reduce((s, [, prests]) => s + prests.reduce((acc, p) => acc + (p.propTTC ?? 0), 0), 0);

  return (
    <div>
      {!embedded && <PageTitle title="Suivi Artistes" subtitle="Activité et historique par artiste" icon={<Mic2 className="text-amber-400" size={24} strokeWidth={1.5} />} />}

      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setMode('actif')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === 'actif' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
          }`}
        >
          Actifs (à venir)
        </button>
        <button
          onClick={() => setMode('passif')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === 'passif' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'bg-slate-900 text-slate-400 border border-slate-800 hover:text-slate-200'
          }`}
        >
          Passifs (passés)
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Artistes</div><div className="font-heading text-3xl text-amber-400">{grouped.length}</div></Card>
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">{mode === 'actif' ? 'Prestations à venir' : 'Prestations passées'}</div><div className="font-heading text-3xl text-sky-400">{grouped.reduce((s, [, p]) => s + p.length, 0)}</div></Card>
        <Card className="p-4"><div className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">CA</div><div className="font-heading text-3xl text-emerald-400">{formatEUR(totalCA)}</div></Card>
      </div>

      <div className="space-y-4">
        {grouped.map(([name, prests]) => {
          const ca = prests.reduce((s, p) => s + (p.propTTC ?? 0), 0);
          const domaines = Array.from(new Set(prests.flatMap((p) => p.domaines)));
          return (
            <Card key={name} className="overflow-hidden">
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div>
                  <div className="font-heading text-lg text-slate-100 flex items-center gap-2"><Mic2 size={16} className="text-amber-400" strokeWidth={1.5} /> {name}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">{domaines.map((d) => <Tag key={d} color="sky">{d}</Tag>)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest">CA</div>
                  <div className="text-amber-400 font-heading text-lg">{formatEUR(ca)}</div>
                  <div className="text-[10px] text-slate-500 mt-1">PRESTA <span className="text-slate-300 font-medium">{prests.length}</span></div>
                </div>
              </div>
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[820px]">
                <thead className="bg-slate-900/40 text-[10px] text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2 text-left">Créée le</th>
                    <th className="px-4 py-2 text-left">Client</th>
                    <th className="px-4 py-2 text-left">Date event</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-left">Statut</th>
                    <th className="px-4 py-2 text-left">Lieu</th>
                    <th className="px-4 py-2 text-right">Prop. TTC</th>
                    <th className="px-4 py-2 text-right w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {prests.map((p) => (
                    <tr key={p.id} className="border-t border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{formatDateTimeFR(p.dateCreation)}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-300">{p.client}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-400">{formatDateFR(p.dateEvent)}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-300">{p.libelle}</td>
                      <td className="px-4 py-2.5"><StatutBadge statut={p.statut} /></td>
                      <td className="px-4 py-2.5 text-xs text-slate-400">{p.lieu || '—'}</td>
                      <td className="px-4 py-2.5 text-xs text-right text-amber-400">{p.propTTC != null ? formatEUR(p.propTTC) : '—'}</td>
                      <td className="px-2 py-2.5 text-right">
                        <button
                          title="Archiver cette proposition"
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Archiver cette proposition ?',
                              description: `Elle disparaîtra du suivi actif (visible dans Archives). ${p.libelle || ''}`,
                              confirmLabel: 'Archiver',
                            });
                            if (!ok) return;
                            await (data as any).archivePrest(p.id);
                            toast.success('Proposition archivée');
                          }}
                          className="p-1.5 rounded-md text-slate-500 hover:text-amber-400 hover:bg-slate-800 transition-colors"
                        >
                          <Archive size={14} strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </Card>
          );
        })}
        {grouped.length === 0 && (
          <Card className="p-8 text-center text-sm text-slate-500">
            Aucun artiste {mode === 'actif' ? 'avec prestation à venir' : 'avec prestation passée'}.
          </Card>
        )}
      </div>
    </div>
  );
}
